import lodash, { throttle } from "lodash";
import { read, utils } from "xlsx";
import { v4 as uuid } from "uuid";
import { getView } from "./utils";
import { Grid } from "./Grid";
import { comnUtils } from "@/comn/utils";
import { GridValidateProps } from "@/comn/hooks/useGrid";
import { useModal } from "@/comn/hooks";
import _ from "lodash";

/**
 * ## Grid Initialize Hook
 * @param props
 * @returns
 */
const useInitialize = (props: any) => {
    const { openModal } = useModal();

    const { _grid, dispatch } = props;
    if (_grid.current._initialized === false) {
        /* Set data  */
        _grid.current._setData = (data: any) => {
            if (!Array.isArray(data?.content)) return;
            _grid.current._data = data;
            const content = data.content.map((_: any) => ({ ..._, __key: uuid(), __type: "origin" }));
            if (_grid.current._pagination === "in") _grid.current._page = 0;
            _grid.current._content = content;
            _grid.current._origin = content;
            getView(_grid);
            _grid.current._originTotalCount = _grid.current._totalCount;

            if (_grid.current._defaultCheck instanceof Function) {
                const checked = content
                    .filter((item: any) => {
                        return _grid.current._defaultCheck(item);
                    })
                    .map(({ __key }: any) => {
                        return __key;
                    });
                _grid.current._checked = checked;
            }

            dispatch({ type: "setData", payload: { _grid } });
        };
        /* Set data to origin */
        _grid.current._resetData = () => {
            _grid.current._content = _grid.current._origin;
            _grid.current._totalCount = _grid.current._originTotalCount;
            getView(_grid);
            dispatch({ type: "resetData", payload: { _grid } });
        };
        /* Clear data */
        _grid.current._clearData = () => {
            _grid.current._content = [];
            _grid.current._origin = [];
            _grid.current._view = [];
            _grid.current._totalCount = 0;
            _grid.current._totalItemCount = 0;
            _grid.current._page = 0;
            _grid.current._size = 10;
            getView(_grid);
            dispatch({ type: "clearData" });
        };
        /* Handle add row */
        _grid.current._handleAdd = (data?: any, position?: any) => {
            if (_grid.current._pagination === "out") return;
            const added = { ...data, __key: uuid(), __type: "added" };
            if (position === "first") {
                _grid.current._content.unshift(added);
            } else {
                _grid.current._content.push(added);
            }

            getView(_grid);
            dispatch({ type: "add", payload: { _grid } });
        };
        /* Handle delete row */
        _grid.current._handleDelete = (type: any) => {
            if (_grid.current._pagination === "out" || !type) return;
            let targets: any[] = [];
            switch (type) {
                case "radio":
                    if (!_grid.current._selectedRow) return;
                    targets.push(_grid.current._selectedRow);
                    break;
                case "checkbox":
                    if (!_grid.current._checked.length) return;
                    targets = [...targets, ..._grid.current._checked];
                    break;
                case "all":
                    if (!_grid.current._selectedRow && !_grid.current._checked.length) return;
                    if (_grid.current._selectedRow) targets.push(_grid.current._selectedRow);
                    if (_grid.current._checked.length) targets = [...targets, ..._grid.current._checked];
                    break;
                default:
                    if (typeof type === "object" && type.__key) targets.push(type.__key);
                    if (Array.isArray(type)) targets = [...targets, ...type.map(({ __key }: any) => __key)];
            }
            _grid.current._content = _grid.current._content
                .map((_: any) => {
                    if (targets.includes(_.__key)) {
                        if (_.__type === "added") return;
                        return { ..._, __type: "deleted" };
                    } else return _;
                })
                .filter((_: any) => _ !== undefined);
            _grid.current._checked = [];
            _grid.current._selectedCel = null;
            _grid.current._selectedRow = null;
            getView(_grid);
            dispatch({ type: "delete", payload: { _grid, type } });
        };

        _grid.current._resetRow = (data: any) => {
            if (!data?.__key) return;
            const __key = data.__key;
            const originRow = _grid.current._origin.find((_: any) => _.__key === __key);
            if (!originRow) return;
            const index = _grid.current._content.findIndex((_: any) => _.__key === __key);
            if (typeof index !== "number") return;
            _grid.current._content[index] = lodash.cloneDeep(originRow);
            getView(_grid);
            dispatch({ type: "resetRow", payload: { _grid } });
        };

        /* Handle update row */
        _grid.current._handleUpdate = (data: any) => {
            if (!data?.__key) return;
            _grid.current._content = _grid.current._content.map((_: any) => {
                if (_.__key !== data.__key) return _;

                const _data = lodash.cloneDeep(data);
                const { __type, __context, __key, ...rest } = _data;

                if (_.__type === "origin" || _.__type === "updated") {
                    const origin = _grid.current._origin.find((o: any) => o.__key === __key);
                    _data.__type = Object.keys(rest).some((k) => {
                        if (
                            (_data[k] && !Array.isArray(_data[k]) && typeof _data[k] === "object") ||
                            (origin[k] && !Array.isArray(origin[k]) && typeof origin[k] === "object")
                        ) {
                            return false;
                        }
                        return _data[k] !== origin[k];
                    })
                        ? "updated"
                        : "origin";
                }

                if (_grid.current._onBeforeUpdate) {
                    _grid.current._onBeforeUpdate(_data);
                }

                if (_data.__type === "origin" || _data.__type === "updated") {
                    const origin = _grid.current._origin.find((o: any) => o.__key === __key);
                    _data.__type = Object.keys(rest).some((k) => {
                        if (
                            (_data[k] && !Array.isArray(_data[k]) && typeof _data[k] === "object") ||
                            (origin[k] && !Array.isArray(origin[k]) && typeof origin[k] === "object")
                        ) {
                            return false;
                        }
                        return _data[k] !== origin[k];
                    })
                        ? "updated"
                        : "origin";
                }

                return _data;
            });
            getView(_grid);
            dispatch({ type: "update", payload: { _grid } });
        };
        /* Handle Sort */
        _grid.current._handleSort = (binding: any) => {
            if (!binding) return;
            let _sort = _grid.current._sort;
            const prev = _sort[binding];
            if (prev) {
                const pval = prev.val;
                const pseq = prev.seq;
                if (pval === "asc") _sort[binding] = { seq: pseq, val: "desc" };
                if (pval === "desc") delete _sort[binding];
            } else {
                const seqs = Object.entries(_sort)
                    .map(([__, v]: any) => v?.seq)
                    .filter((_) => _ !== undefined);
                const nseq = seqs.length === 0 ? 0 : Math.max(...seqs) + 1;
                _sort[binding] = { seq: nseq, val: "asc" };
            }
            _sort = Object.fromEntries(
                lodash
                    .sortBy(Object.entries(_sort), [(a: any) => a[1].seq])
                    .map(([k, v]: any, i: any) => [k, { ...v, seq: i }]),
            );
            _grid.current._sort = _sort;
            getView(_grid);
            dispatch({ type: "sort", payload: { _grid, binding } });
        };
        /* Handle Group */
        _grid.current._handleGroup = (groupKey: string, open: boolean) => {
            if (!groupKey) return;
            _grid.current._groupStatus[groupKey] = { ..._grid.current._groupStatus[groupKey], open };
            getView(_grid);
            dispatch({ type: "group", payload: { _grid } });
        };
        /* Set edit */
        _grid.current._setEdit = (type: any, target: any, value: any) => {
            switch (type) {
                case "column":
                    _grid.current._body = _grid.current._body.map((_: any) => {
                        if (_.id !== target) return _;
                        const cells = _.cells.map((__: any) => {
                            return { ...__, edit: value };
                        });
                        return { ..._, cells };
                    });
                    break;
                case "cell":
                    _grid.current._body = _grid.current._body.map((_: any) => {
                        const cells = _.cells.map((__: any) => {
                            if (__.binding !== target) return __;
                            return { ...__, edit: value };
                        });
                        return { ..._, cells };
                    });
                    break;
                case "row": {
                    const key =
                        typeof target === "object" && !!target?.__key
                            ? target.__key
                            : typeof target === "string"
                              ? target
                              : undefined;
                    if (key) {
                        const row = _grid.current._editingRow.find((r: any) => r.key === key && r.cell === undefined);
                        if (typeof value === "boolean") {
                            _grid.current._editingRow = row
                                ? _grid.current._editingRow.map((_: any) => {
                                      if (_.key !== key) return _;
                                      if (_.cell !== undefined) return _;
                                      return { ..._, edit: value };
                                  })
                                : [..._grid.current._editingRow, { key, edit: value }];
                        }
                    }
                    break;
                }
                case "rowCell": {
                    const { row, cell } = target;
                    const rowKey =
                        typeof row === "object" && !!row?.__key ? row.__key : typeof row === "string" ? row : undefined;
                    if (rowKey) {
                        const row = _grid.current._editingRow.find((r: any) => r.key === rowKey && r.cell === cell);
                        if (typeof value === "boolean") {
                            _grid.current._editingRow = row
                                ? _grid.current._editingRow.map((_: any) => {
                                      if (_.key !== rowKey) return _;
                                      if (_.cell !== cell) return _;
                                      return { ..._, edit: value };
                                  })
                                : [..._grid.current._editingRow, { key: rowKey, edit: value, cell }];
                        }
                    }
                    break;
                }
            }
            dispatch({ type: "setEdit", payload: { _grid } });
        };
        /* Set show */
        _grid.current._setShow = (type: any, target: any, value: any) => {
            switch (type) {
                case "column": {
                    _grid.current._head = _grid.current._head.map((_: any) => {
                        if (_.id !== target) return _;
                        return {
                            ..._,
                            show: value,
                            cells: _.cells.map((__: any) => {
                                return { ...__, show: value };
                            }),
                        };
                    });
                    _grid.current._body = _grid.current._body.map((_: any) => {
                        if (_.id !== target) return _;
                        return {
                            ..._,
                            show: value,
                            cells: _.cells.map((__: any) => {
                                return { ...__, show: value };
                            }),
                        };
                    });
                }
            }
            dispatch({ type: "setShow", payload: { _grid } });
        };
        /* Set option */
        _grid.current._setOption = (target: any, value: any) => {
            switch (target) {
                case "height": {
                    if (value === "auto") {
                        _grid.current._height = _grid.current._listInner.clientHeight;
                        _grid.current._autoHeight = true;
                    } else {
                        _grid.current._height = value;
                        _grid.current._autoHeight = false;
                    }
                    break;
                }
                case "edit": {
                    _grid.current._edit = value;
                    _grid.current._body = _grid.current._body.map((_: any) => {
                        return {
                            ..._,
                            edit: value,
                            cells: _.cells.map((__: any) => {
                                return { ...__, edit: value };
                            }),
                        };
                    });
                    break;
                }
                default:
                    _grid.current[`_${target}`] = value;
                    break;
            }
            dispatch({ type: "setOption", payload: { _grid, target } });
        };
        /* Handle Click Cell */
        _grid.current._handleClickCel = (payload: any) => {
            const { key, binding, value, formattedValue, rowValues } = payload;
            if (_grid.current._onCellClick)
                _grid.current._onCellClick({
                    binding,
                    value,
                    formattedValue,
                    rowValues,
                });
            if (_grid.current._selectedCel?.__key !== key) {
                _grid.current._selectedCel = {
                    value,
                    binding,
                    rowValues,
                    formattedValue,
                    __key: key,
                };
                dispatch({ type: "handleClickCel", payload: { _grid } });
            }
        };
        /* Handle Select Row (Radio)  */
        _grid.current._handleSelect = (event: any, rowKey: any) => {
            _grid.current._selectedRow = rowKey;

            if (_grid.current._onRowSelect) {
                _grid.current._onRowSelect(_grid.current._content.find(({ __key }: any) => __key === rowKey));
            }

            dispatch({ type: "handleSelect", payload: { _grid } });
        };
        /* Handle Check Row (Checkbox) */
        _grid.current._handleCheck = (event: any, rowKey: any) => {
            let _checked = [..._grid.current._checked];
            _checked = event.target.checked ? [..._checked, rowKey] : _checked.filter((_: any) => _ !== rowKey);
            _grid.current._checked = _checked;

            if (_grid.current._onRowCheck) {
                _grid.current._onRowCheck(
                    _grid.current._content.find(({ __key }: any) => __key === rowKey),
                    event.target.checked,
                );
            }

            dispatch({ type: "handleCheck", payload: { _grid } });
        };
        /* Handle Check All Row (Checkbox) */
        _grid.current._handleCheckAll = (event: any) => {
            if (event.target.checked) {
                let checkedContent;

                if (_grid.current._render?.checkbox) {
                    checkedContent = _grid.current._view.filter((_: any) => _grid.current._render?.checkbox(_));
                } else {
                    checkedContent = _grid.current._view;
                }

                _grid.current._checked = checkedContent.map(({ __key }: any) => __key);

                if (_grid.current._onRowCheck) {
                    checkedContent.forEach((_: any) => {
                        _grid.current._onRowCheck(_, event.target.checked);
                    });
                }
            } else {
                let checkedContent = _grid.current._content.filter((_: any) => {
                    return _grid.current._checked.includes(_.__key);
                });

                _grid.current._checked = [];

                if (_grid.current._onRowCheck) {
                    checkedContent.forEach((_: any) => {
                        _grid.current._onRowCheck(_, event.target.checked);
                    });
                }
            }

            dispatch({ type: "handleCheckAll", payload: { _grid } });
        };

        _grid.current._unCheck = (target?: any) => {
            if (target === undefined) {
                _grid.current._checked = [];
            }
            dispatch({ type: "unCheck", payload: { _grid } });
        };
        _grid.current._unSelectRow = () => {
            _grid.current._selectedRow = null;
            dispatch({ type: "unSelectRow", payload: { _grid } });
        };
        _grid.current._unSelectCel = () => {
            _grid.current._selectedCel = null;
            dispatch({ type: "unSelectCel", payload: { _grid } });
        };

        /* Handle Change Page */
        _grid.current._handlePage = (next: any) => {
            _grid.current._page = next;
            // _grid.current._checked = [];
            // _grid.current._selectedRow = null;
            // _grid.current._selectedCel = null;
            if (_grid.current._pagination === "out") {
                _grid.current._setPage(next);
            } else if (_grid.current._pagination === "in") {
                getView(_grid);
                dispatch({ type: "handleChangePage", payload: { _grid } });
            }
        };
        /* Handle Change Size */
        _grid.current._handleSize = (next: any) => {
            _grid.current._page = 0;
            _grid.current._size = next;
            // _grid.current._checked = [];
            // _grid.current._selectedRow = null;
            // _grid.current._selectedCel = null;
            if (_grid.current._pagination === "out") {
                _grid.current._setPage(0);
                _grid.current._setSize(next);
            } else if (_grid.current._pagination === "in") {
                getView(_grid);
                dispatch({ type: "handleChangeSize", payload: { _grid } });
            }
        };
        /* Readjust */
        _grid.current._readjustHeight = lodash.debounce(() => {
            // if (_grid.current._height === _grid.current._listInner.clientHeight) return;
            // _grid.current._height = _grid.current._listInner.clientHeight;
            dispatch({ type: "readjustHeight", payload: { _grid } });
        }, 50);

        _grid.current._setSchema = () => {
            dispatch({ type: "setSchema", payload: { _grid } });
        };
        _grid.current._setHead = () => {
            dispatch({ type: "setHead", payload: { _grid } });
        };
        _grid.current._setBody = () => {
            dispatch({ type: "setBody", payload: { _grid } });
        };
        _grid.current._setGroup = () => {
            getView(_grid);
            dispatch({ type: "setGroup", payload: { _grid } });
        };

        _grid.current._setHeadHeight = throttle((height: number) => {
            _grid.current._headHeight = height;
            dispatch({ type: "setHeadHeight", payload: { _grid } });
        }, 200);

        _grid.current._setGroupFoot = () => {
            getView(_grid);
            dispatch({ type: "setGroupFoot", payload: { _grid } });
        };
        _grid.current._validate = (arg?: GridValidateProps) => {
            const _head = _grid.current._head;
            const _body = _grid.current._body;
            const labels: { [key: string]: any } = {};
            const bodys: { [key: string]: any } = {};
            _head.map((item: any) => {
                item.cells?.map((_item: any) => {
                    labels[_item.binding] = _item.header;
                });
            });

            _body.map((item: any) => {
                item.cells?.map((_item: any) => {
                    bodys[_item.binding] = _item;
                });
            });

            const errors = _grid.current._content
                .filter((_: any) => {
                    return _.__type !== "deleted";
                })
                .reduce((prev: any, row: any) => {
                    for (const binding in _grid.current._rule) {
                        const bindingValue = row[binding];
                        const rules = _grid.current._rule[binding];

                        for (let i = 0; i < rules.length; i++) {
                            let invalid = false;
                            const { value, type } = rules[i];

                            switch (type) {
                                case "required":
                                    invalid =
                                        bodys[binding]?.type !== "number"
                                            ? !bindingValue
                                            : bindingValue === undefined || bindingValue === null;
                                    //invalid = !bindingValue;
                                    break;
                                case "min":
                                    invalid = bindingValue < value;
                                    break;
                                case "max":
                                    invalid = bindingValue > value;
                                    break;
                                case "minLength":
                                    invalid = bindingValue?.length < value;
                                    break;
                                case "maxLength":
                                    invalid = bindingValue?.length > value;
                                    break;
                                case "pattern":
                                    invalid = !value.test(bindingValue);
                                    break;
                                case "validate":
                                    invalid = !value(bindingValue, row);
                                    break;
                                case "resource":
                                    break;
                            }

                            console.log(invalid);

                            if (
                                (rules[i].type == "min" || rules[i].type == "max") &&
                                (bodys[binding]?.type === "date" ||
                                    bodys[binding]?.type === "datetime" ||
                                    bodys[binding]?.type === "time")
                            ) {
                                rules[i].value = comnUtils.getViewValue(value, bodys[binding]);
                            }

                            if (invalid) {
                                prev.push({
                                    ...rules[i],
                                    gridName: arg?.name ? arg.name : undefined,
                                    binding,
                                    label: labels[binding],
                                    input: comnUtils.getViewValue(bindingValue, bodys[binding]),
                                    row: row.__index + 1,
                                });
                            }
                        }

                        if (arg?.validate) {
                            //arg.validate(row) !== undefined ){
                        }
                    }
                    return prev;
                }, []);

            return errors && errors.length > 0 ? { errors } : undefined;
        };

        _grid.current._importExcel = (arg?: any) => {
            return new Promise((resolve) => {
                const input = document.createElement("input");
                input.type = "file";
                input.onchange = async () => {
                    if (!input.files) return;
                    const file = input.files[0];
                    const buffer = await file.arrayBuffer();
                    const wb = read(buffer, { type: "binary", cellDates: true });
                    const ws = wb.Sheets[wb.SheetNames[arg?.sheet ? arg?.sheet : 0]];

                    const bindingColumn: any = {};
                    const bindingType: any = {};
                    for (const cell of _grid.current._body.flatMap(({ cells }: any) => cells)) {
                        if (cell.excel && cell.binding) {
                            bindingColumn[cell.binding] = cell.excel;
                            bindingType[cell.binding] = cell.type;
                        }
                    }

                    if (arg?.bindings) {
                        for (const col in arg.bindings) {
                            bindingColumn[arg.bindings[col]] = col;
                            //todo type check
                        }
                    }

                    const raw = utils
                        .sheet_to_json(ws, { header: "A", raw: false })
                        .map(({ __rowNum__, ..._ }: any) => {
                            const next = { ...bindingColumn };
                            for (const col in next) {
                                next[col] = _[next[col]];
                            }
                            return next;
                        });

                    if (arg?.headers) {
                        for (let i = 0; i < arg?.headers; i++) {
                            raw.shift();
                        }
                    } else {
                        raw.shift();
                    }

                    for (let i = 0; i < raw.length; i++) {
                        let item = raw[i];

                        for (const [key, value] of Object.entries(item)) {
                            if (bindingType[key] === "number" && value !== undefined && value !== null) {
                                try {
                                    item[key] = Number(String(value).replaceAll(",", ""));
                                } catch {
                                    item[key] = undefined;
                                }
                            } else if (
                                bindingType[key] === "date" ||
                                bindingType[key] === "datetime" ||
                                bindingType[key] === "time"
                            ) {
                                try {
                                    if (!comnUtils.isValidDate(String(value))) {
                                        item[key] = undefined;
                                    }
                                } catch (e) {
                                    item[key] = undefined;
                                }
                            }
                        }
                    }

                    resolve(raw);
                };
                input.click();
            });
        };

        _grid.current._importExcels = (args?: any) => {
            return new Promise((resolve) => {
                const input = document.createElement("input");
                input.type = "file";
                input.onchange = async () => {
                    if (!input.files) return;
                    const file = input.files[0];
                    const buffer = await file.arrayBuffer();
                    const wb = read(buffer, { type: "binary", cellDates: true });

                    const raws = [];

                    for (let i = 0; i < args.length; i++) {
                        const arg = args[i];

                        const ws = wb.Sheets[wb.SheetNames[arg?.sheet ? arg?.sheet : 0]];

                        if (!ws) {
                            raws.push([]);
                            continue;
                        }

                        const bindingColumn: any = {};
                        const bindingType: any = {};
                        for (const cell of _grid.current._body.flatMap(({ cells }: any) => cells)) {
                            if (cell.excel && cell.binding) {
                                bindingColumn[cell.binding] = cell.excel;
                                bindingType[cell.binding] = cell.type;
                            }
                        }

                        if (arg?.bindings) {
                            for (const col in arg.bindings) {
                                bindingColumn[arg.bindings[col]] = col;
                            }
                        }

                        const raw = utils
                            .sheet_to_json(ws, { header: "A", raw: false })
                            .map(({ __rowNum__, ..._ }: any) => {
                                const next = { ...bindingColumn };
                                for (const col in next) {
                                    next[col] = _[next[col]];
                                }
                                return next;
                            });

                        if (arg?.headers) {
                            for (let i = 0; i < arg?.headers; i++) {
                                raw.shift();
                            }
                        } else {
                            raw.shift();
                        }

                        for (let i = 0; i < raw.length; i++) {
                            let item = raw[i];

                            for (const [key, value] of Object.entries(item)) {
                                if (bindingType[key] === "number" && value !== undefined && value !== null) {
                                    try {
                                        item[key] = Number(String(value).replaceAll(",", ""));
                                    } catch {
                                        item[key] = undefined;
                                    }
                                } else if (
                                    bindingType[key] === "date" ||
                                    bindingType[key] === "datetime" ||
                                    bindingType[key] === "time"
                                ) {
                                    try {
                                        if (!comnUtils.isValidDate(String(value))) {
                                            item[key] = undefined;
                                        }
                                    } catch (e) {
                                        item[key] = undefined;
                                    }
                                }
                            }
                        }

                        raws.push(raw);
                    }

                    resolve(raws);
                };
                input.click();
            });
        };

        _grid.current._upRow = (row: any) => {
            let target = [];
            if (_.isArray(row)) {
                target = row;
            } else {
                target = [row];
            }

            for (let i = 0; i < target.length; i++) {
                const _row = target[i];

                const viewIndex = _grid.current._view.findIndex(({ __key }: any) => __key === _row?.__key);
                const upper = _grid.current._view[viewIndex - 1];
                if (!upper?.__key) break;

                const contentIndex = _grid.current._content.findIndex(({ __key }: any) => __key === _row?.__key);
                const upperContentIndex = _grid.current._content.findIndex(({ __key }: any) => __key === upper?.__key);

                _grid.current._content[contentIndex] = upper;
                _grid.current._content[upperContentIndex] = _row;
                getView(_grid);
            }

            dispatch({ type: "upRow", payload: { _grid } });
        };

        _grid.current._downRow = (row: any) => {
            let target = [];
            if (_.isArray(row)) {
                target = row;
            } else {
                target = [row];
            }

            //if (!row?.__key) return;

            for (let i = target.length - 1; i >= 0; i--) {
                const _row = target[i];
                const viewIndex = _grid.current._view.findIndex(({ __key }: any) => __key === _row?.__key);
                const upper = _grid.current._view[viewIndex + 1];
                if (!upper?.__key) break;

                const contentIndex = _grid.current._content.findIndex(({ __key }: any) => __key === _row?.__key);
                const upperContentIndex = _grid.current._content.findIndex(({ __key }: any) => __key === upper?.__key);

                _grid.current._content[contentIndex] = upper;
                _grid.current._content[upperContentIndex] = _row;

                getView(_grid);
            }
            dispatch({ type: "upRow", payload: { _grid } });
        };

        _grid.current._checkRow = (row: any) => {
            if (!row?.__key) return;
            const rowKey = row.__key;

            let _checked = [..._grid.current._checked, row.__key];
            _grid.current._checked = _checked;

            if (_grid.current._onRowCheck) {
                _grid.current._onRowCheck(
                    _grid.current._content.find(({ __key }: any) => __key === rowKey),
                    true,
                );
            }

            dispatch({ type: "handleCheck", payload: { _grid } });
        };

        _grid.current._selectRow = (data: any) => {
            const isIndex = typeof data === "number";
            const isRow = Boolean(data?.__key);

            if (!isIndex && !isRow) return;

            let item: any;
            if (isIndex) item = _grid.current._content.find(({ __index }: any) => __index === data);
            if (isRow) item = _grid.current._content.find(({ __key }: any) => __key === data.__key);

            if (!item) return;

            if (_grid.current._onRowSelect) {
                _grid.current._onRowSelect(item, true);
            }

            _grid.current._selectedRow = item.__key;
            dispatch({ type: "handleSelect", payload: { _grid } });
        };

        _grid.current._downloadTemplate = comnUtils.downloadTemplate;

        _grid.current._unCheckRow = (row: any) => {
            if (!row?.__key) return;
            const rowKey = row.__key;

            let _checked = _grid.current._checked.filter((key: any) => key !== rowKey);
            _grid.current._checked = _checked;

            if (_grid.current._onRowCheck) {
                _grid.current._onRowCheck(
                    _grid.current._content.find(({ __key }: any) => __key === rowKey),
                    false,
                );
            }

            dispatch({ type: "handleCheck", payload: { _grid } });
        };

        _grid.current._refresh = () => {
            dispatch({ type: "refresh" });
        };

        _grid.current._openAmendment = (row: any, row2: any) => {
            const obj = {
                current: {
                    ..._grid.current,
                    _initialized: false,
                    _checkbox: false,
                    _radio: false,
                    _index: false,
                    _edit: false,
                    _add: false,
                    _delete: false,
                    _pagination: false,
                    _group: {},
                    _groupSchema: undefined,
                    _groupFootSchema: undefined,
                    _data: null,
                    _origin: [],
                    _content: [],
                    _view: [],
                    _height: _grid.current._headHeight,
                    _autoHeight: true,
                },
            };

            openModal({
                size: "lg",
                content: <Grid _grid={obj} data={{ content: [row, row2] }} />,
            });
        };

        _grid.current._scrollToItem = (index: any) => {
            _grid.current._listRef?.scrollToItem?.(index, "center");
        };

        /* Initialize Grid */
        if (_grid.current._queue.length) {
            _grid.current._queue.forEach((fn: any) => fn());
        }
        _grid.current._initialized = true;
        _grid.current._handleAddRange = (rows: any[], position?: any) => {
            if (_grid.current._pagination === "out") return;
            if (!rows.length) return;

            const added = rows.map((data) => ({ ...data, __key: uuid(), __type: "added" }));

            _grid.current._content =
                position === "first" ? added.concat(_grid.current._content) : _grid.current._content.concat(added);

            getView(_grid);
            dispatch({ type: "add", payload: { _grid } });
        };
    }
};

export { useInitialize };
